import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import profilePhotoSmall from "@/assets/profile-photo-small.webp";
import profilePhotoLarge from "@/assets/profile-photo-large.webp";

const links = [
  { label: "About", href: "#about" },
  { label: "Tech Stack", href: "#tech" },
  { label: "Projects", href: "#projects" },
  { label: "Experience", href: "#experience" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <button
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 group"
            aria-label="View profile photo"
          >
            <div className="relative">
              <div className="absolute -inset-[3px] rounded-full bg-gradient-to-tr from-primary via-amber-400 to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <img
                src={profilePhotoSmall}
                alt="Rohit Shrivastava - Senior Software Engineer"
                width={32}
                height={32}
                className="relative w-8 h-8 rounded-full object-cover ring-2 ring-transparent group-hover:ring-0 transition-all duration-300 cursor-pointer"
              />
            </div>
          </button>

          {/* Desktop */}
          <ul className="hidden md:flex gap-6">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center min-h-[48px] px-1"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-foreground min-w-[48px] min-h-[48px] flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden bg-background border-b border-border/50"
            >
              <ul className="flex flex-col gap-1 px-6 py-4">
                {links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpen(false);
                        const targetId = l.href.replace(/.*#/, "");
                        const elem = document.getElementById(targetId);
                        window.history.pushState(null, "", l.href);
                        if (elem) {
                          // Wait for the menu close animation to finish before scrolling
                          // DOM changes during smooth scrolling cancel the scroll on mobile browsers
                          setTimeout(() => {
                            elem.scrollIntoView({ behavior: "smooth" });
                          }, 350);
                        }
                      }}
                      className="text-muted-foreground hover:text-primary transition-colors block py-3 min-h-[48px] flex items-center"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Instagram-style profile photo preview */}
      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm cursor-pointer"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient ring */}
              <div className="rounded-full p-[4px] bg-gradient-to-tr from-primary via-amber-400 to-accent">
                <div className="rounded-full p-[3px] bg-background">
                  <img
                    src={profilePhotoLarge}
                    alt="Rohit Shrivastava - Profile photo"
                    width={320}
                    height={320}
                    loading="lazy"
                    className="w-64 h-64 sm:w-80 sm:h-80 rounded-full object-cover"
                  />
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowPreview(false)}
                className="absolute -top-2 -right-2 bg-background/90 border border-border rounded-full p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
